import { WaitHint } from "./WaitHint.tsx";
import { useEffect, useState, useSyncExternalStore } from "react";
import { api } from "../api.ts";
import { getCurrentCustomer, subscribeCurrentCustomer } from "../currentCustomer.ts";
import { goCustomer, goCustomers, goKnowledge } from "../navigate.ts";
import { notifyChanges } from "../changes.ts";
import { Icon } from "./Workspace.tsx";

/**
 * AI 销售助手(FR-9)。悬浮面板,收起时只有一个按钮。
 *
 * 面板里**答案分两种颜色**:
 *   · 数据(蓝):日程、我的数、客户进展、跟进统计 —— 直接从库里来的,没经过模型
 *   · 知识(灰):要从资料里组织的,带出处,点得开看依据
 * 分开是有意的 —— 销售要知道哪句话是系统查出来的、哪句话是模型写的。
 * 全都长一样的助手,用户最后会对两种都半信半疑。
 */
interface Turn {
  q: string;
  a: string;
  kind: "data" | "knowledge" | "blocked" | "action" | "confirmation" | "suggestion";
  /** data 路答的是哪类问题;stats(跟进统计)附「去客户列表查看」。 */
  intent?: "schedule" | "customer_progress" | "my_numbers" | "stats";
  citations: { documentId: string | null; partition: string | null; documentTitle: string; snippet: string }[];
  operationId?: string;
  autonomyLevel?: number;
  undoableUntil?: string;
  draft?: { subject: string; body: string };
  /** 问题点了客户的名、助手按那一家答的。 */
  customer?: { id: string; name: string };
  /** 知识库里查不到,回答只凭通用销售方法,不是这家公司的资料。 */
  generic?: boolean;
}
interface Operation { id: string; customerName: string; description: string; status: string; expired: boolean; result: (Turn & { answer: string }) | null }

const QUICK = ["我本周该跟谁?", "我上周跟进了多少家客户?", "这个客户进展到哪了?", "客户嫌贵怎么回应?", "帮我起草跟进邮件", "帮我安排明天跟进"];

export function Assistant(): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [turns, setTurns] = useState<Turn[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [operations, setOperations] = useState<Operation[]>([]);
  const [copied, setCopied] = useState<number>();

  const customer = useSyncExternalStore(subscribeCurrentCustomer, getCurrentCustomer, () => undefined);
  const loadOperations = async (): Promise<void> => {
    const r = await api.get<{ operations: Operation[] }>(`/api/assistant/operations${customer ? `?customerId=${customer.id}` : ""}`);
    setOperations(r.operations);
  };
  useEffect(() => {
    if (!open) return;
    let alive = true;
    const load = () => { void api.get<{ operations: Operation[] }>(`/api/assistant/operations${customer ? `?customerId=${customer.id}` : ""}`)
      .then((r) => { if (alive) setOperations(r.operations); }).catch((e) => { if (alive) setErr((e as Error).message); }); };
    load(); const timer = setInterval(() => { if (document.visibilityState === "visible") load(); }, 8000);
    return () => { alive = false; clearInterval(timer); };
  }, [open, customer?.id]);

  // Esc 关面板 —— 悬浮层挡住内容时,第一反应是按 Esc
  useEffect(() => {
    if (!open) return;
    document.body.classList.add("assistant-open");
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.classList.remove("assistant-open"); window.removeEventListener("keydown", onKey); };
  }, [open]);

  const ask = async (question: string): Promise<void> => {
    if (!question.trim() || busy) return;
    setBusy(true); setErr(""); setQ("");
    try {
      const r = await api.post<Turn & { answer: string }>("/api/assistant/ask", {
        question: question.trim(),
        ...(customer ? { customerId: customer.id } : {}),
      });
      setTurns((t) => [...t, { ...r, q: question.trim(), a: r.answer, citations: r.citations ?? [] }]);
      if (r.kind === "action") notifyChanges();
      await loadOperations();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  const change = async (index: number, action: "confirm" | "undo"): Promise<void> => {
    const turn = turns[index]; if (!turn?.operationId || busy) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post<Turn & { answer: string }>(`/api/assistant/operations/${turn.operationId}/${action}`);
      setTurns((old) => old.map((t, i) => i === index
        ? action === "undo" ? { ...t, kind: "data", a: r.answer, undoableUntil: undefined }
          : { ...t, ...r, a: r.answer } : t));
      notifyChanges();
      await loadOperations();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  const confirmSaved = async (operation: Operation): Promise<void> => {
    if (busy) return; setBusy(true); setErr("");
    try {
      const r = await api.post<Turn & { answer: string }>(`/api/assistant/operations/${operation.id}/confirm`, {});
      setTurns((t) => [...t, { ...r, q: `${operation.customerName}：${operation.description}`, a: r.answer, citations: r.citations ?? [] }]);
      notifyChanges(); await loadOperations();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  if (!open) {
    // 原型里这是右侧贴边的竖标签,不是右下角的圆钮 —— 圆钮会压住内容,
    // 而这一屏底部恰好是动作卡的按钮区
    return (
      <button className="ai-toggle" aria-label="打开 AI 助理" onClick={() => setOpen(true)}><Icon name="sparkles" size={17} />AI 助理</button>
    );
  }

  return (
    <div className="ai-panel" role="complementary" aria-label="AI 销售助手">
      <div className="panel-head">
        <div className="panel-title">
          <b><Icon name="sparkles" size={18} /> AI 销售助理</b>
          {customer && <span className="panel-context" title={customer.name}>正在看 {customer.name}</span>}
        </div>
        <button className="panel-close" aria-label="关闭 AI 助手" onClick={() => setOpen(false)}><Icon name="close" /></button>
      </div>

      <div className="panel-body">
        {operations.length > 0 && <details><summary>最近操作与待确认({operations.length})</summary>
          {operations.map((o) => <div key={o.id} className="hint"><b>{o.customerName}</b><p>{o.description}</p>
            {o.expired ? <span>操作已失效(客户已结算或已过期)。</span> : o.status === "awaiting_confirmation" || o.status === "suggested"
              ? <button className="b p" disabled={busy} onClick={() => void confirmSaved(o)}>确认这项操作</button>
              : o.status === "completed" && o.result ? <button className="b" onClick={() => setTurns((t) => [...t, {
                ...o.result!, q: `${o.customerName}：${o.description}`, a: o.result!.answer, citations: o.result!.citations ?? [],
              }])}>{o.result.draft ? "查看已保存草稿" : "查看执行结果"}</button>
              : <span>{o.status === "undone" ? "已撤销" : o.status === "blocked" ? "未执行" : o.status === "failed" ? "处理失败,请重新发起" : "正在处理"}</span>}
          </div>)}
        </details>}
        {turns.length === 0 && (
          <div className="assistant-welcome"><span className="empty-symbol"><Icon name="sparkles" size={25} /></span><h3>{customer ? "一起准备下一次跟进" : "你的销售工作助理"}</h3><p>查询进展、查找知识、起草消息。打开客户详情后，也可以安排跟进。</p><span className="tag tBrand">依据团队资料回答 · 操作按权限确认</span></div>
        )}
        {turns.map((t, i) => (
          <div key={i} style={{ marginBottom: 12 }}>
            <div className="msg me" style={{ maxWidth: "100%", marginBottom: 6 }}>{t.q}</div>
            <div className="msg ai" style={{ maxWidth: "100%", whiteSpace: "pre-wrap" }}>
              {t.a}
              <div style={{ marginTop: 6 }}>
                <span className={`tag ${t.kind === "data" ? "tA" : t.generic ? "tW" : "tN"}`}>
                  {t.kind === "data" ? "业务数据" : t.kind === "action" ? "已执行"
                    : t.kind === "confirmation" ? "需要你确认" : t.kind === "blocked" ? "未执行"
                    : t.generic ? "通用方法 · 不是你们公司的资料" : "引用团队知识"}
                </span>
              </div>
              {t.citations.length > 0 && (
                <div className="hint" style={{ marginTop: 6 }}>
                  {t.citations.map((c, j) => (
                    <div key={j}>{c.documentId ? <button className="b sm q" onClick={() => goKnowledge(c.partition ?? undefined, c.documentId!)}>📖 《{c.documentTitle}》</button> : `📖 ${c.documentTitle}`}:{c.snippet.slice(0, 80)}…</div>
                  ))}
                </div>
              )}
              {t.draft && <div className="hint" style={{ whiteSpace: "pre-wrap" }}>
                <b>{t.draft.subject}</b><p>{t.draft.body}</p>
                <button className="b" onClick={() => { void navigator.clipboard.writeText(`${t.draft!.subject}\n\n${t.draft!.body}`)
                  .then(() => setCopied(i)).catch(() => setErr("未能写入剪贴板,请手动选中草稿复制")); }}>{copied === i ? "已复制" : "复制草稿"}</button>
                <p className="muted">仅内部草稿,尚未发送。价格与合同条款请由人确认。</p>
              </div>}
              {t.customer && <button className="b sm q" style={{ marginTop: 6 }} onClick={() => { setOpen(false); goCustomer(t.customer!.id); }}>打开 {t.customer.name}</button>}
              {t.intent === "stats" && <button className="b sm q" style={{ marginTop: 6 }} onClick={() => { setOpen(false); goCustomers(); }}>去客户列表查看</button>}
              {t.kind === "confirmation" && <button className="b p" disabled={busy} onClick={() => void change(i, "confirm")}>确认执行</button>}
              {t.undoableUntil && new Date(t.undoableUntil) > new Date() && <button className="b q" disabled={busy} onClick={() => void change(i, "undo")}>撤销这个操作</button>}
            </div>
          </div>
        ))}
        {busy && <p className="muted">思考中…</p>}
        <WaitHint active={busy} working="AI 正在思考" />
        {err && <div className="err">{err}</div>}
      </div>

      <div className="panel-foot">
        <div className="quick" style={{ marginBottom: 6 }}>
          {QUICK.map((s) => (
            <button key={s} className="b sm" disabled={busy} onClick={() => { void ask(s); }}>{s}</button>
          ))}
        </div>
        <div className="form-row row">
          <input className="in" aria-label="向 AI 助理提问" style={{ flex: 1 }} placeholder={customer ? "询问这个客户，或安排下一步…" : "查询日程、业绩或销售知识…"} value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void ask(q); }} />
          <button className="b p" disabled={busy || !q.trim()} onClick={() => { void ask(q); }}>发送</button>
        </div>
      </div>
    </div>
  );
}
